import {
  ButtonTypes,
  ButtonVariants,
  Button,
  ButtonSizes
} from "@certa/catalyst/components/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@certa/catalyst/components/Dropdown";
import {
  TypographyColors,
  TypographyVariants,
  Typography,
  Ellipsis
} from "@certa/catalyst/components/Typography";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { ChevronLeft } from "@certa/icons/components/ChevronLeft";
import { Close } from "@certa/icons/components/Close";
import { Dots } from "@certa/icons/components/Dots";
import {
  CommentObjectTypes,
  type CommentChannel,
  type ConversationThread,
  type WorkflowFamily
} from "@certa/types";
import { css } from "emotion";
import { useIntl } from "react-intl";
import { handleOnPrintComment } from "../commentsNew/SingleChannelView/SingleChannelView";
import { useScreenResolution } from "@certa/common/hooks/useScreenResolution";
import { useCommsNewEndpoints } from "@certa/common/toggles";
import { Box } from "@certa/catalyst/layouts/Box";
import { useGetWorkflowDetails } from "@certa/queries/queries/workflowDetails/getWorkflowDetails.query";
import { AnalyticsEvents, analyticsService } from "@certa/analytics";
import { useCommentsDrawerContext } from "../../CommentsDrawerContext";
import { useParams } from "react-router";

export const DrawerTitle = ({
  isMultiChannel,
  channels,
  workflowFamily,
  fieldName,
  activeChannel,
  activeConversationThreadComms,
  setActiveChannel,
  setActiveConversationThreadComms,
  showBackButton = false,
  onClose
}: {
  isMultiChannel: boolean;
  channels: CommentChannel[] | undefined;
  workflowFamily: WorkflowFamily | undefined;
  fieldName: string | undefined;
  activeChannel: CommentChannel | null;
  activeConversationThreadComms: ConversationThread | null;
  setActiveChannel: (activeChannel: CommentChannel | null) => void;
  setActiveConversationThreadComms: (
    conversationThread: ConversationThread | null
  ) => void;
  showBackButton?: boolean;
  onClose: () => void;
}) => {
  const intl = useIntl();

  const { isNonEditable, setShouldShowNewThread, isReadOnly } =
    useCommentsDrawerContext();

  const isCommsNewEndpointEnabled = useCommsNewEndpoints();

  const { id: workflowId } = useParams();

  const isOnMultiChannelView =
    isMultiChannel &&
    (isCommsNewEndpointEnabled
      ? !activeConversationThreadComms ||
        activeConversationThreadComms.objectType ===
          CommentObjectTypes.CONSOLIDATED
      : !activeChannel);

  const { data: workflowDetails } = useGetWorkflowDetails(
    channels?.[0]?.workflowId ?? Number(workflowId)
  );

  const stepGroupName =
    activeChannel?.stepGroupName || channels?.[0]?.stepGroupName;
  const stepName = activeChannel?.stepName || channels?.[0]?.stepName;

  const workflowName = workflowDetails?.name;
  const kindName = workflowDetails?.definition.name;

  const titleForDashboardAndReports = workflowFamily
    ?.map(workflow => workflow.name)
    .join(": ");

  const commentsDrawerTitle = titleForDashboardAndReports ? (
    titleForDashboardAndReports
  ) : (
    <Stack direction="horizontal" gap="4">
      {workflowName && (
        <span style={{ maxWidth: "10rem", fontWeight: "bold" }}>
          <Ellipsis>{workflowName}</Ellipsis>
        </span>
      )}{" "}
      - {kindName && <Ellipsis>{kindName}</Ellipsis>}
    </Stack>
  );

  const { isMobileResolution } = useScreenResolution();

  const mobileResolutionPadding = isOnMultiChannelView ? "8 4 8 16" : "12 8";

  const desktopResolutionPadding = "0 20 0 24";

  return (
    <Box width="100%" borderBottom="1px solid var(--colors-neutral-400)">
      <Stack
        direction="horizontal"
        align="center"
        justify="space-between"
        padding={
          isMobileResolution
            ? mobileResolutionPadding
            : desktopResolutionPadding
        }
        gap="8"
        dangerouslySetClassName={drawerTitleWrapperStyles(
          isOnMultiChannelView,
          isMobileResolution
        )}
      >
        <Stack
          direction="horizontal"
          gap="4"
          dangerouslySetClassName={drawerTitleStyles}
        >
          {showBackButton && !isOnMultiChannelView && (
            <Button
              aria-label="Back button"
              leftIcon={<ChevronLeft />}
              variant={ButtonVariants.TEXT}
              size={
                isMobileResolution ? ButtonSizes.SMALL : ButtonSizes.DEFAULT
              }
              onClick={() => {
                if (isMultiChannel) {
                  if (activeChannel) {
                    setActiveChannel(null);
                  }
                  if (activeConversationThreadComms) {
                    setActiveConversationThreadComms(null);
                  }
                } else {
                  onClose();
                }
              }}
            />
          )}
          <Stack
            direction="vertical"
            align="start"
            justify={fieldName ? "flex-start" : "center"}
            width={
              showBackButton && !isOnMultiChannelView
                ? isMobileResolution
                  ? "85%"
                  : "90%"
                : "100%"
            }
            gap="4"
          >
            <Typography
              className={css({
                maxWidth: "100%",
                textWrap: "nowrap"
              })}
              variant={TypographyVariants.BODY_BOLD}
              color={TypographyColors.NEUTRAL_800}
            >
              {commentsDrawerTitle}
            </Typography>
            {!isOnMultiChannelView && (
              <Typography
                variant={TypographyVariants.BODY}
                color={TypographyColors.NEUTRAL_700}
                width="100%"
              >
                <Stack direction="horizontal" gap="4">
                  {stepGroupName && (
                    <span style={{ maxWidth: "10rem" }}>
                      <Ellipsis>{stepGroupName}</Ellipsis>
                    </span>
                  )}

                  {stepName && (
                    <>
                      /
                      <span
                        style={{
                          ...(fieldName && { maxWidth: "10rem" }),
                          ...(!fieldName && { fontWeight: "bold" })
                        }}
                      >
                        <Ellipsis>{stepName}</Ellipsis>
                      </span>
                    </>
                  )}

                  {fieldName && (
                    <>
                      /
                      <span
                        style={{
                          minWidth: 0,
                          maxWidth: "25rem",
                          fontWeight: "bold"
                        }}
                      >
                        <Ellipsis>{fieldName}</Ellipsis>
                      </span>
                    </>
                  )}
                </Stack>
              </Typography>
            )}
          </Stack>
        </Stack>
        <div className={drawerActionButtonsStyles}>
          {(!isMultiChannel || !!activeChannel) && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <div>
                  <Button
                    aria-label="Meta actions"
                    leftIcon={<Dots />}
                    type={ButtonTypes.ICON}
                  />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" width="10.5rem">
                {!isReadOnly && !isNonEditable && (
                  <DropdownMenuItem
                    value="addNewGroup"
                    onSelect={() => {
                      analyticsService.track(
                        AnalyticsEvents.workflowDetailEvents
                          .COMMENT_DETAIL_PANE_CLICK_OPEN_ADD_GROUP_MODAL
                      );
                      setShouldShowNewThread(true);
                    }}
                  >
                    {intl.formatMessage({
                      id: "comments.addNewGroup",
                      defaultMessage: "Add Group"
                    })}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  value="print"
                  onSelect={() => {
                    handleOnPrintComment();
                  }}
                >
                  {intl.formatMessage({
                    id: "stepBodyFormInstances.printText",
                    defaultMessage: "Print"
                  })}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button
            aria-label="Close comments drawer"
            leftIcon={<Close size={18} />}
            type={ButtonTypes.ICON}
            onClick={onClose}
          />
        </div>
      </Stack>
    </Box>
  );
};

const drawerTitleWrapperStyles = (
  isOnMultiChannelView: boolean,
  isMobileResolution: boolean
) => {
  return css({
    height: isMobileResolution ? "" : "5rem",
    flexShrink: 0
  });
};

const drawerActionButtonsStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "1rem"
});

const drawerTitleStyles = css({
  flex: "1",
  minWidth: "0"
});
